import { useTranslation } from "react-i18next";
import { PMDialog } from "../../../utils/PMDialog";
import { PMButton } from "../../../utils/PMButton";
import { ChevronDownIcon } from "@heroicons/react/24/outline";

function formatGuideAnswer(question, value, t) {
  if (value === undefined || value === null || value === "") return "";
  const options = question.options || [];
  if (Array.isArray(value)) {
    return value
      .map((item) => options.find((option) => option.value === item)?.label || item)
      .join(", ");
  }
  if (typeof value === "boolean") return value ? t("Ja") : t("Nei");
  return options.find((option) => option.value === value)?.label || String(value);
}

function formatRunDate(iso, locale) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(locale, { dateStyle: "short", timeStyle: "short" });
}

export default function ReportGuideHistoryDialog({
  open,
  onClose,
  runs,
  canStartMore,
  onStartGuide,
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || undefined;

  const handleStart = () => {
    onClose();
    if (onStartGuide) onStartGuide();
  };

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title="report.view.guideHistory.title"
      size="3xl"
      zIndex={2600}
    >
      <div className="space-y-4">
        {!runs?.length ? (
          <p className="text-sm text-gray-600">{t("report.view.guideHistory.empty")}</p>
        ) : (
          <ul className="space-y-3">
            {runs.map((run) => {
              const questions = run?.template?.questions || [];
              const answers = run?.answers || {};
              const tagCount = run?.effects_applied?.tag_ids?.length || 0;
              const title = run?.template?.title || t("report.view.sectionGuide.title");
              const who =
                run?.user?.displayTitle || run?.user?.name || t("report.view.guideHistory.unknownUser");

              return (
                <li key={run.id}>
                  <details className="group rounded-xl border border-gray-200 bg-gray-50/40 open:bg-white">
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-left [&::-webkit-details-marker]:hidden">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-gray-900">{title}</div>
                        <div className="mt-0.5 text-xs text-gray-500">
                          {t("report.view.guideHistory.completedAt")}:{" "}
                          {formatRunDate(run.completed_at || run.updated_at, locale)}
                          {" · "}
                          {t("report.view.guideHistory.by")} {who}
                        </div>
                      </div>
                      <ChevronDownIcon className="h-5 w-5 shrink-0 text-gray-400 transition-transform group-open:rotate-180" />
                    </summary>
                    <div className="space-y-3 border-t border-gray-100 px-4 pb-4 pt-3">
                      {questions.length > 0 ? (
                        <div>
                          <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                            {t("report.view.guideHistory.answers")}
                          </p>
                          <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                            {questions.map((question) => (
                              <div
                                key={question.key}
                                className="rounded-lg border border-gray-100 bg-white p-3"
                              >
                                <p className="text-xs font-medium text-gray-500">{question.label}</p>
                                <p className="mt-1 text-sm text-gray-900 break-words">
                                  {formatGuideAnswer(question, answers[question.key], t) || "–"}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null}
                      {tagCount > 0 ? (
                        <p className="text-xs text-emerald-800">
                          {t("report.view.guideHistory.tagsApplied", { count: tagCount })}
                        </p>
                      ) : null}
                    </div>
                  </details>
                </li>
              );
            })}
          </ul>
        )}

        <div className="flex flex-wrap justify-end gap-2 border-t border-gray-100 pt-4">
          {canStartMore ? (
            <PMButton type="button" onClick={handleStart}>
              {t("report.view.guideHistory.startGuide")}
            </PMButton>
          ) : null}
          <button
            type="button"
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            onClick={onClose}
          >
            {t("report.view.guideHistory.close")}
          </button>
        </div>
      </div>
    </PMDialog>
  );
}
